import { SocialIdentity } from "@/services/bedrock";
import React, { useState } from "react";
import { Avatar } from "@/components/ui";
import { StarRating } from "@/components/reviews";
import { extractInitials, kFormatter, printDate, useAccountSettings, useSocialCoachSession } from "@/utils";
import { SocialProfileReviewsModal } from "@/components/SocialNetworkProfiles/components/SocialProfileReviewsModal";
import { WebSettings } from "@/interfaces/dtos/CoachAppSetting";
import { Settings } from "lucide-react";
import { isReviewNetwork } from "./shared";

interface Props {
  socialIdentity: SocialIdentity;
  userId: string | number;
  onEditReviewPrefs?: () => void;
}

export function SocialProfileConnectedState({ socialIdentity, userId, onEditReviewPrefs }: Props) {
  const [showReviewsModal, setShowReviewsModal] = useState(false);

  type StatType = {
    label: string;
    beginningValue: number;
    latestValue: number | string | React.ReactNode;
    growth: number;
    action?: () => void;
  };
  const { coachId } = useSocialCoachSession();
  const { hasAccountSetting } = useAccountSettings(coachId);
  const hasReputationManagement = hasAccountSetting(WebSettings.REPUTATION_MANAGEMENT);
  const showReviewPrefs = hasReputationManagement && isReviewNetwork(socialIdentity.socialNetworkId);
  const stats: StatType[] = [];
  if (socialIdentity.followerCount !== null) {
    const latestValue = socialIdentity.followerCount || 0;
    const beginningValue = socialIdentity.beginningFollowerCount || 0;
    const growth = latestValue - beginningValue;
    const label = socialIdentity.socialNetworkId === "LI" ? "CONNECTIONS" : "FOLLOWERS";
    stats.push({ label, beginningValue, latestValue, growth });
  }
  const showReviews = () => setShowReviewsModal(true);
  if (socialIdentity.reviewCount !== null && hasReputationManagement) {
    const latestValue = socialIdentity.reviewCount || 0;
    const beginningValue = socialIdentity.beginningReviewCount || 0;
    const growth = latestValue - beginningValue;
    const label = "REVIEWS";
    stats.push({ label, beginningValue, latestValue, growth, action: showReviews });
  }
  if (socialIdentity.avgRating !== null && hasReputationManagement) {
    const latestValue = Math.round((Number(socialIdentity.avgRating) || 0) * 100) / 100;
    const beginningValue = Math.round((Number(socialIdentity.beginningAvgRating) || 0) * 100) / 100;
    const growth = Math.round((latestValue - beginningValue) * 100) / 100;
    const label = "AVERAGE RATING";
    stats.push({
      label,
      beginningValue,
      latestValue: <StarRating rating={latestValue} size="sm" />,
      growth,
      action: showReviews,
    });
  }

  const urlLabel =
    socialIdentity.socialNetworkId === "GL"
      ? "View in Google Maps"
      : socialIdentity.username
      ? `@${socialIdentity.username}`
      : "View Profile";

  return (
    <>
      <div className="flex flex-col bg-slate-50 rounded-2xl p-2 border border-slate-100">
        <div className="flex items-center gap-3">
          <Avatar size="lg" className="ring-1 ring-slate-200 shadow-sm">
            <Avatar.Image src={socialIdentity.profileImageUrl} alt="Profile" />
            <Avatar.Fallback>{extractInitials(socialIdentity.displayName)}</Avatar.Fallback>
          </Avatar>

          <div className="flex flex-col leading-tight overflow-hidden">
            <span className="font-semibold text-base text-slate-900 truncate">{socialIdentity.displayName}</span>
            <a
              href={socialIdentity.publicUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-slate-500 hover:text-primary transition-colors truncate mt-0.5"
            >
              {urlLabel}
            </a>
          </div>
        </div>

        <div className="mt-2 pt-2 px-2 border-t border-slate-100 flex items-start gap-4">
          {stats.map(stat => (
            <div className="flex flex-col" key={stat.label}>
              <span className="text-[11px] font-bold text-slate-400 flex items-center gap-1">
                {stat.label}
                {stat.label === "REVIEWS" && showReviewPrefs && (
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      onEditReviewPrefs?.();
                    }}
                    className="text-slate-400 hover:text-slate-600 transition-colors p-0.5 rounded focus:outline-none cursor-pointer"
                    title="Review Settings"
                  >
                    <Settings className="size-3" />
                  </button>
                )}
              </span>
              <div className="flex items-center h-7">
                {stat.action ? (
                  <div className="flex items-center gap-1.5 cursor-pointer" onClick={stat.action}>
                    {typeof stat.latestValue === "number" || typeof stat.latestValue === "string" ? (
                      <span className="text-lg font-bold text-primary">{stat.latestValue}</span>
                    ) : (
                      stat.latestValue
                    )}
                    {stat.growth > 0 && (
                      <span className="text-sm font-semibold text-emerald-500">+{kFormatter(stat.growth)}</span>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    {typeof stat.latestValue === "number" || typeof stat.latestValue === "string" ? (
                      <span className="text-lg font-bold text-slate-900">{stat.latestValue}</span>
                    ) : (
                      stat.latestValue
                    )}
                    {stat.growth > 0 && (
                      <span className="text-sm font-semibold text-emerald-500">+{kFormatter(stat.growth)}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          {hasReputationManagement && (
            <SocialProfileReviewsModal
              open={showReviewsModal}
              userId={userId}
              socialIdentity={socialIdentity}
              onClose={() => setShowReviewsModal(false)}
            />
          )}
          <div className="flex-grow-2" />
          <div className="flex flex-col text-right">
            <span className="text-[11px] font-bold text-slate-400">LINKED</span>
            <span className="text-sm font-semibold text-slate-600">{printDate(socialIdentity.createdDate)}</span>
          </div>
        </div>
      </div>
    </>
  );
}
